/* eslint-disable max-lines */

import { gql } from '@apollo/client/core'

import { PAGE_INFO_ATTRIBUTES } from './fragments'
import { CATEGORY_ATTRIBUTE_ATTRIBUTES } from './fragments/CategoryAttribute'
import { CATEGORY_SEGMENT_DETAILED_ATTRIBUTES } from './fragments/CategorySegment'
import { LINE_ITEM_PRODUCT_BASE_ATTRIBUTES } from './fragments/LineItem'
import { LINE_ITEM_FINANCIAL_ATTRIBUTES } from './fragments/LineItem'
import { UNIT_ATTRIBUTES } from './fragments/Unit'

const FETCH_LINE_ITEM_QUERY = gql`
  query fetchLineItem(
    $id: ID!
    $includeCategorySegment: Boolean = true
    $includeCategoryAttributes: Boolean = false
    $includeSubItems: Boolean = false
    $withFinancials: Boolean = false
    $includeRental: Boolean = false
    $includeProductTemplate: Boolean = false
    $includeLocationStops: Boolean = false
    $lineItemType: LineItemTypeEnum
    $attachmentScope: [AttachmentScope!] = [not_virtual, product] # used in categorySegmentDetailedAttributes
  ) {
    lineItem(id: $id) {
      id
      name
      categorySegmentId
      annotation
      imageUrl
      startDate
      startTime
      returnDate
      returnTime
      rentalId
      rentalCode
      earliestAvailableDate
      earliestAvailableDateTime
      checkupEnabled
      filterStatus
      vacant
      lineItemType
      machineStatus
      rental @include(if: $includeRental) {
        isSuccessful
      }
      locationStops @include(if: $includeLocationStops) {
        id
        latitude
        longitude
        firstRecordedAt
        lastRecordedAt
      }
      project {
        id
        name
        latitude
        longitude
        address {
          id
          address
          address2
          zip
          city
          country
        }
      }
      ...lineItemFinancialAttributes @include(if: $withFinancials)
      attributeValues @include(if: $includeCategoryAttributes) {
        id
        displayValue
        attributeDetailId
      }
      referencedByLineItems(lineItemType: $lineItemType) {
        id
        name
        startDate
        returnDate
        lineItemType
        quantity
        quantityEstimated
        validDisplayUnit
        displayUnitWithQuantity
        displayDiscountedUnitPrice
        discountedUnitPrice
        total
        requestStatus
        unit {
          id
          name
        }
        categorySegment @include(if: $includeCategorySegment) {
          quantityMandatory
        }
      }
      subItems @include(if: $includeSubItems) {
        id
        name
        imageUrl
        startDate
        returnDate
        earliestAvailableDate
        earliestAvailableDateTime
        lineItemType
        freeAttachment
      }
      productTemplate @include(if: $includeProductTemplate) {
        id
        name
        dataSheetUrl
        manufacturer
        maschineType
        attributeValues {
          id
          displayValue
          displayMetric
          attributeDetail {
            id
            descriptionShort
            attributeType
          }
        }
      }
      categorySegment @include(if: $includeCategorySegment) {
        id
        name
        imageUrl
        minimumRentalTime
        minimumRentalDuration
        ...categorySegmentDetailedAttributes
        unit {
          ...unitAttributes
        }
        category @include(if: $includeCategoryAttributes) {
          categoryAttributes {
            ...categoryAttributeAttributes
          }
        }
      }
    }
  }
  ${LINE_ITEM_FINANCIAL_ATTRIBUTES}
  ${CATEGORY_SEGMENT_DETAILED_ATTRIBUTES}
  ${CATEGORY_ATTRIBUTE_ATTRIBUTES}
  ${UNIT_ATTRIBUTES}
`

const FETCH_ALL_LINE_ITEMS = gql`
  query fetchAllLineItems(
    $lineItemType: [LineItemTypeEnum!]
    $projectId: ID
    $excludeSubItems: Boolean = true
    $limit: Int
    $requested: Boolean = true
    $excludeCanceled: Boolean = true
    $scopes: [LineItemScope!]
    $period: [ProductPeriodEnum!]
    $sort: [SortOptions!] = [{ sortField: "created_at", sortDirection: "desc" }]
    $first: Int
    $last: Int
    $offset: Int
    $after: String
    $before: String
  ) {
    lineItemsPaginated(
      lineItemType: $lineItemType
      projectId: $projectId
      excludeSubItems: $excludeSubItems
      sort: $sort
      limit: $limit
      requested: $requested
      excludeCanceled: $excludeCanceled
      scopes: $scopes
      period: $period
      first: $first
      last: $last
      offset: $offset
      after: $after
      before: $before
    ) {
      pageInfo {
        ...pageInfoAttributes
      }
      totalCount
      maxPageSize
      pageIndex
      nodes {
        id
        name
        startDate
        returnDate
        startTime
        returnTime
        rentalId
        #        status #not used
        rentalCode
        project {
          id
          name
        }
        filterStatus
        discountedUnitPrice
        displayDiscountedUnitPrice
        displayUnitWithQuantity
        validDisplayUnit
        quantity
        total
        unit {
          id
          name
        }
        subItems @include(if: $excludeSubItems) {
          discountAsPercentage
          discountedUnitPrice
          displayDiscountedUnitPrice
          displayUnitWithQuantity
          filterStatus
          freeAttachment
          lineItemType
          machineStatus
          name
          quantity
          returnDate
          returnTime
          startDate
          startTime
          total
          unit {
            id
            name
          }
          validDisplayUnit
        }
        ... on SubItem {
          machineStatus
          freeAttachment
          lineItemId
        }
        ... on ProductLineItemInterface {
          #          imageUrl #not used
          machineStatus
          categorySegment {
            name
          }
          product {
            name
          }
        }
      }
    }
  }
  ${PAGE_INFO_ATTRIBUTES}
`

// Projection for the schedulers (project plan + timeline). They only render the
// machine name, the rental/project label and the date range, so fetching the
// financial fields and the nested sub items of fetchAllLineItems is dead weight.
const FETCH_LINE_ITEMS_FOR_SCHEDULER = gql`
  query fetchLineItemsForScheduler(
    $lineItemType: [LineItemTypeEnum!]
    $projectId: ID
    $excludeSubItems: Boolean = true
    $requested: Boolean = true
    $excludeCanceled: Boolean = true
    $period: [ProductPeriodEnum!]
    $sort: [SortOptions!] = [{ sortField: "created_at", sortDirection: "desc" }]
    $first: Int
    $offset: Int
  ) {
    lineItemsPaginated(
      lineItemType: $lineItemType
      projectId: $projectId
      excludeSubItems: $excludeSubItems
      requested: $requested
      excludeCanceled: $excludeCanceled
      period: $period
      sort: $sort
      first: $first
      offset: $offset
    ) {
      pageInfo {
        ...pageInfoAttributes
      }
      totalCount
      maxPageSize
      nodes {
        id
        name
        status
        startDate
        returnDate
        rentalId
        rentalCode
        project {
          id
          name
        }
        ... on ProductLineItemInterface {
          categorySegment {
            name
          }
          product {
            name
          }
        }
      }
    }
  }
  ${PAGE_INFO_ATTRIBUTES}
`

const UPDATE_LINE_ITEM = gql`
  mutation updateCustomerMachineDetails($input: UpdateCustomerMachineDetailsInput!) {
    updateCustomerMachineDetails(input: $input) {
      lineItem {
        ...lineItemProductBaseAttributes
      }
      errors {
        message
      }
    }
  }
  ${LINE_ITEM_PRODUCT_BASE_ATTRIBUTES}
`

const CREATE_LINE_ITEM = gql`
  mutation createCustomerLineItem($input: CreateCustomerLineItemInput!) {
    createCustomerLineItem(input: $input) {
      lineItem {
        ...lineItemProductBaseAttributes
      }
      errors {
        message
      }
    }
  }
  ${LINE_ITEM_PRODUCT_BASE_ATTRIBUTES}
`

const DELETE_LINE_ITEM = gql`
  mutation deleteLineItem($input: DeleteLineItemInput!) {
    deleteLineItem(input: $input) {
      lineItem {
        id
        name
      }
      errors {
        path
        message
      }
    }
  }
`

export {
  FETCH_LINE_ITEM_QUERY,
  UPDATE_LINE_ITEM,
  CREATE_LINE_ITEM,
  DELETE_LINE_ITEM,
  FETCH_ALL_LINE_ITEMS,
  FETCH_LINE_ITEMS_FOR_SCHEDULER
}
